import { EntryTemplate } from '@/components/templates/EntryTemplate';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { OAUTH_AUTHORIZE_PATH, PLATFORM_REDIRECT_DELAY_MS } from '@/lib/constants';
import { getLocale, translations } from '@/lib/i18n';
import { readKnownPlatforms, saveKnownPlatforms } from '@/lib/knownPlatforms';
import { readOAuthAuthorizeRequest, subdomainFromPlatformUrlHint } from '@/lib/oauthAuthorize';
import { fetchSupportedPlatformHosts, validatePlatform } from '@/lib/platformApi';
import type { KnownPlatform, PlatformResponse } from '@/lib/platformTypes';
import { KnownPlatformsPage } from '@/pages/KnownPlatformsPage';
import { SubdomainPage } from '@/pages/SubdomainPage';
import { TransitionPage } from '@/pages/TransitionPage';
import { useLocaleStore } from '@/store/locale';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import './index.css';

type View = 'known' | 'subdomain';

export function App() {
	const savedLocale = useLocaleStore((state) => state.locale);
	const locale = window.location.pathname === OAUTH_AUTHORIZE_PATH ? (savedLocale ?? getLocale()) : getLocale();
	const t = translations[locale];
	const [platforms, setPlatforms] = useState<KnownPlatform[]>(readKnownPlatforms);
	const [view, setView] = useState<View>(() => (readKnownPlatforms().length ? 'known' : 'subdomain'));
	const [transition, setTransition] = useState<PlatformResponse>();
	const [platformHost, setPlatformHost] = useState<string>();
	const automaticHintAttempted = useRef(false);
	const selectionAttempt = useRef(0);
	const redirectTimeout = useRef<number | undefined>(undefined);
	const oauthRequest = useMemo(() => readOAuthAuthorizeRequest(window.location), []);
	const isOAuthAuthorizeRoute = window.location.pathname === OAUTH_AUTHORIZE_PATH;
	const invalidOAuthRequest = isOAuthAuthorizeRoute && !oauthRequest;

	useEffect(() => {
		saveKnownPlatforms(platforms);
	}, [platforms]);

	useEffect(() => {
		const cancelSelection = () => {
			selectionAttempt.current += 1;
			window.clearTimeout(redirectTimeout.current);
		};
		const dismissTransition = () => {
			cancelSelection();
			setTransition(undefined);
			setView(readKnownPlatforms().length ? 'known' : 'subdomain');
		};

		const restorePage = (event: PageTransitionEvent) => {
			if (event.persisted) dismissTransition();
		};

		window.addEventListener('pagehide', dismissTransition);
		window.addEventListener('pageshow', restorePage);

		return () => {
			cancelSelection();
			window.removeEventListener('pagehide', dismissTransition);
			window.removeEventListener('pageshow', restorePage);
		};
	}, []);

	useEffect(() => {
		if (invalidOAuthRequest) return;

		let isMounted = true;
		const storedPlatforms = readKnownPlatforms();

		void Promise.all(
			storedPlatforms.map(
				async (platform) => (await validatePlatform(platform.subdomain, t)).platform ?? platform,
			),
		).then((refreshedPlatforms) => {
			if (!isMounted) return;

			setPlatforms((previous) =>
				previous.map(
					(platform) => refreshedPlatforms.find((item) => item.subdomain === platform.subdomain) ?? platform,
				),
			);
		});

		return () => {
			isMounted = false;
		};
	}, [invalidOAuthRequest, t]);

	const selectPlatform = useCallback(
		async (subdomain: string) => {
			if (isOAuthAuthorizeRoute && !oauthRequest) return { error: t.unknown };

			automaticHintAttempted.current = true;
			const attempt = ++selectionAttempt.current;
			window.clearTimeout(redirectTimeout.current);
			setTransition(undefined);

			const result = await validatePlatform(subdomain, t, oauthRequest);
			if (attempt !== selectionAttempt.current) return { error: t.unknown };
			if (!result.platform) return result;
			const destination = oauthRequest ? result.response.authorization_url : `${result.response.redirect_url}/#/`;
			if (!destination) return { error: t.unknown };

			const platform = result.platform;
			setPlatforms((previous) => [platform, ...previous.filter((item) => item.subdomain !== platform.subdomain)]);
			setView('known');
			setTransition(result.response);
			redirectTimeout.current = window.setTimeout(
				() => window.location.assign(destination),
				PLATFORM_REDIRECT_DELAY_MS,
			);
			return result;
		},
		[isOAuthAuthorizeRoute, oauthRequest, t],
	);

	useEffect(() => {
		let isMounted = true;

		void fetchSupportedPlatformHosts()
			.then(([host]) => {
				if (!isMounted) return;

				setPlatformHost(host);
				if (!oauthRequest || automaticHintAttempted.current) return;

				const subdomain = subdomainFromPlatformUrlHint(window.location, [host]);
				if (!subdomain) return;

				automaticHintAttempted.current = true;
				void selectPlatform(subdomain);
			})
			.catch((error: unknown) => {
				console.error('Unable to fetch supported platform hosts.', error);
			});

		return () => {
			isMounted = false;
		};
	}, [oauthRequest, selectPlatform]);

	const forgetPlatform = (platform: KnownPlatform) => {
		setPlatforms((previous) => previous.filter((item) => item.subdomain !== platform.subdomain));
	};

	return (
		<EntryTemplate locale={locale}>
			{invalidOAuthRequest ? (
				<Alert
					className="max-w-xl"
					variant="destructive"
				>
					<AlertDescription>{t.invalidOAuthRequest}</AlertDescription>
				</Alert>
			) : view === 'known' && platforms.length > 0 ? (
				<KnownPlatformsPage
					platforms={platforms}
					translation={t}
					onSelect={selectPlatform}
					onForget={forgetPlatform}
					onUseAnother={() => setView('subdomain')}
				/>
			) : (
				<SubdomainPage
					hasKnownPlatforms={platforms.length > 0}
					platformHost={platformHost}
					translation={t}
					onSubmit={selectPlatform}
					onBack={() => setView('known')}
				/>
			)}
			{transition && (
				<TransitionPage
					translation={t}
					platform={transition}
				/>
			)}
		</EntryTemplate>
	);
}
